import React, { Component } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { action, computed, observable, makeObservable } from 'mobx'
import { observer } from 'mobx-react'

import { isEmpty } from '@elo-kit/utils/validators.utils'
import { convertFromBytes } from '@elo-kit/utils/files.utils'
import { formatDateTime } from '@elo-kit/utils/dateTime.utils'
import { Ellipsis } from '@elo-kit/components/ellipsis/Ellipsis'
import { CheckboxField } from '@elo-kit/components/form/checkbox-field/CheckboxField'
import { RadioField } from '@elo-kit/components/form/radio-field/RadioField'
import { I18nContext } from '@elo-kit/components/i18n/i18n'
import { SIZES } from '@elo-kit/constants/general.constants'

import { mapToIdsArray } from 'utils/helpers.utils'

import EloModal from 'ui/EloModal'

import EloTable from '../elo-table/EloTable'
import FilePreviewIcon from './FilePreviewIcon'
import TableSortLabelWrap from '../TableSortLabelWrap'

const getTableHead = (I18n) => [
  {
    label: '',
  },
  {
    label: I18n.t('react.cabinet.common.name'),
  },
  {
    label: I18n.t('react.cabinet.common.size'),
  },
  {
    label: I18n.t('react.cabinet.common.uploaded'),
  },
]

const propTypes = {
  toggle: PropTypes.func.isRequired,
  modal: PropTypes.bool.isRequired,
  fetchFileList: PropTypes.func.isRequired,
  fileList: PropTypes.array,
  saveFiles: PropTypes.func.isRequired,
  allowMultiple: PropTypes.bool,
  acceptedFiles: PropTypes.string,
  allowMediathek: PropTypes.bool,
  modalTitle: PropTypes.string,
}

@observer
class FileSelectionModal extends Component {
  @observable selectedFiles = {}

  static contextType = I18nContext

  constructor(props) {
    super(props)
    makeObservable(this)
  }

  componentDidMount() {
    const { fetchFileList, initStore } = this.props

    initStore.toggleFiltersDataStoringInUrl(false)
    fetchFileList().then(() => {
      const { fileList, good = {} } = this.props
      const { id } = good.digital || {}
      const selectedFile = fileList.find((file) => file.id === Number(id))

      if (id) this.setSelectedFiles({ [id]: selectedFile })
    })
  }

  @computed get disableSave() {
    return isEmpty(this.selectedFiles)
  }

  @action setSelectedFiles = (value) => (this.selectedFiles = value)

  @action selectFile = ({ target }) => {
    const { allowMultiple, fileList } = this.props
    const { checked, dataset, name } = target

    const files = fileList.slice()
    const fileId = allowMultiple ? name : dataset.fileId

    if (checked) {
      const file = files.find(({ id }) => id === Number(fileId))
      const selectedFile = { [fileId]: file }
      const selectedFiles = allowMultiple
        ? {
            ...this.selectedFiles,
            ...selectedFile,
          }
        : selectedFile

      this.setSelectedFiles(selectedFiles)
    } else {
      const { [fileId]: fileToRemove, ...remainFiles } = this.selectedFiles
      this.setSelectedFiles(remainFiles)
    }
  }

  handleSave = () => {
    const { saveFiles } = this.props

    saveFiles(this.selectedFiles)
  }

  @computed get getModalBody() {
    const { allowMultiple, fileList, coverId, disableRadioButtonCondition = () => false } = this.props

    const selectedFileId = allowMultiple
      ? mapToIdsArray(fileList.filter((item) => item.hideCheckBox))
      : (fileList.find((item) => item.hideCheckBox || item.id === coverId) || {}).id

    return (
      fileList &&
      fileList.map((item) => {
        // Handle different file object structure for covers/digitals
        const file = item.file
          ? {
              ...item,
              ...item.file,
            }
          : item
        const { hideCheckBox, id, fileFileSize, size, wistiaData, name, createdAt } = file
        const selectedFile = this.selectedFiles[id]
        const checked = selectedFile && selectedFile.id
        const videoName = (wistiaData || {}).name
        const fileName = item.fileName || item.name || name || videoName

        const nameClasses = classNames('file-item-name', {
          'file-item-name--bold': checked && !hideCheckBox,
        })
        const tableTdClasses = classNames({
          'elo-table__small-td radio-button-column radio-button-column--small-top-margin': !allowMultiple,
          'checkbox-button-column': allowMultiple,
        })

        return (
          <tr key={id}>
            <td className={tableTdClasses}>
              {allowMultiple ? (
                <CheckboxField
                  data-file-id={id}
                  checked={checked || selectedFileId.includes(id)}
                  disabled={selectedFileId.includes(id)}
                  onChange={this.selectFile}
                  name={id}
                />
              ) : (
                <RadioField
                  id={id}
                  value={checked || selectedFileId}
                  onChange={this.selectFile}
                  disabled={hideCheckBox || id === coverId || disableRadioButtonCondition(item)}
                  data-file-id={id}
                />
              )}
            </td>
            <td className='elo-table__file-item'>
              <div className='file-item-left-side'>
                <FilePreviewIcon file={file} disabled={disableRadioButtonCondition(item)} />
                <div className={nameClasses}>
                  <Ellipsis maxLength={40}>{fileName}</Ellipsis>
                </div>
              </div>
            </td>
            <td className='elo-table__file-item'>
              {!!(!wistiaData && (fileFileSize || size)) && (
                <div className='file-item-size'>{convertFromBytes(fileFileSize || size)}</div>
              )}
            </td>
            <td>{formatDateTime(createdAt)}</td>
          </tr>
        )
      })
    )
  }
  render() {
    const { allowMultiple, initStore, modal, modalTitle, toggle } = this.props
    const I18n = this.context

    return (
      <EloModal
        isOpen={modal}
        toggle={toggle}
        title={modalTitle}
        size={SIZES.large}
        className='modal-dialog file-choser-modal'
        submit={this.handleSave}
        submitDisabled={this.disableSave}
        submitButtonChild={I18n.t('react.shared.button.select')}
        zIndex={1100}
      >
        <EloTable
          initStore={initStore}
          filtersList={['createdAt']}
          tableModalScroll
          tableWithRadios={!allowMultiple}
          tableWithCheckbox={allowMultiple}
          scrollIntoView
        >
          <EloTable.Head>
            <TableSortLabelWrap list={getTableHead(I18n)} store={initStore} />
          </EloTable.Head>
          <EloTable.Body>{this.getModalBody}</EloTable.Body>
        </EloTable>
      </EloModal>
    )
  }
}

FileSelectionModal.displayName = 'FileSelectionModal'
FileSelectionModal.propTypes = propTypes

export default FileSelectionModal
